import { Store } from "../domain/Store";
import { Shopper } from "../domain/Shopper";
import { Delivery } from "../domain/Delivery";
import { Admin } from "../domain/Admin";
import { client } from "@/client";

export class HttpStoreRepository {
  private BASE_URL = `${client.OPENBASS}/v1/stores`;

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const res = await fetch(url, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
      ...options,
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.response?.error || data?.error || "Request failed");
    }

    return data.response as T;
  }

  /** ---------------- STORES ---------------- */
  async getAll(): Promise<Store[]> {
    return this.request<Store[]>(this.BASE_URL);
  }

  async getById(id: string): Promise<Store | null> {
    return this.request<Store | null>(`${this.BASE_URL}/${id}`);
  }

  async create(store: Store): Promise<Store> {
    return this.request<Store>(this.BASE_URL, {
      method: "POST",
      body: JSON.stringify(store),
    });
  }

  async update(id: string, store: Store): Promise<Store | null> {
    return this.request<Store | null>(`${this.BASE_URL}/${id}`, {
      method: "PUT",
      body: JSON.stringify(store),
    });
  }

  async delete(id: string): Promise<void> {
    await this.request<void>(`${this.BASE_URL}/${id}`, { method: "DELETE" });
  }

  /** ---------------- SHOPPERS ---------------- */
  async getShoperId(id: string): Promise<Shopper | null> {
    return this.request<Shopper | null>(`${this.BASE_URL}/shoppers/${id}`);
  }

  async getShoppersByStoreId(storeId: string): Promise<Shopper[]> {
    return this.request<Shopper[]>(`${this.BASE_URL}/${storeId}/shoppers`);
  }

  async addShopper(shopper: Shopper): Promise<void> {
    await this.request<void>(`${this.BASE_URL}/shoppers`, {
      method: "POST",
      body: JSON.stringify(shopper),
    });
  }

  async updateShopper(
    id: string,
    shopper: Partial<Shopper>
  ): Promise<Shopper | null> {
    return this.request<Shopper | null>(`${this.BASE_URL}/shoppers/${id}`, {
      method: "PUT",
      body: JSON.stringify(shopper),
    });
  }

  async deleteShopperId(shopperId: string): Promise<void> {
    await this.request<void>(`${this.BASE_URL}/shoppers/${shopperId}`, {
      method: "DELETE",
    });
  }

  /** ---------------- ADMINS ---------------- */
  async getAdminId(id: string): Promise<Admin | null> {
    return this.request<Admin | null>(`${this.BASE_URL}/admins/${id}`);
  }

  /** ---------------- DELIVERIES ---------------- */
  async getDeliveryId(id: string): Promise<Delivery | null> {
    return this.request<Delivery | null>(`${this.BASE_URL}/deliveries/${id}`);
  }

  async addDelivery(delivery: Delivery): Promise<void> {
    await this.request<void>(`${this.BASE_URL}/deliveries`, {
      method: "POST",
      body: JSON.stringify(delivery),
    });
  }

  async updateDelivery(
    id: string,
    delivery: Delivery
  ): Promise<Delivery | null> {
    return this.request<Delivery | null>(`${this.BASE_URL}/deliveries/${id}`, {
      method: "PUT",
      body: JSON.stringify(delivery),
    });
  }

  async deleteDelivery(deliveryId: string): Promise<void> {
    await this.request<void>(`${this.BASE_URL}/deliveries/${deliveryId}`, {
      method: "DELETE",
    });
  }
}
